import React from 'react';
import { motion } from 'framer-motion';
import { 
  Landmark, 
  Shield, 
  Lock, 
  FileCheck, 
  Server, 
  Users,
  Network,
  Cloud,
  CheckCircle,
  ArrowRight,
  Database,
  AlertTriangle
} from 'lucide-react';

interface GovernmentProps {
  setCurrentPage: (page: string) => void;
}

const Government: React.FC<GovernmentProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Critical Infrastructure Protection",
      description: "Advanced security measures to protect government infrastructure and critical systems.",
      items: [
        "Infrastructure monitoring",
        "Threat detection & response",
        "Access control systems",
        "Security hardening"
      ]
    },
    {
      icon: Lock,
      title: "Classified Data Security",
      description: "Comprehensive protection for sensitive government data and classified information.",
      items: [
        "Data encryption",
        "Access management",
        "Secure storage",
        "Data loss prevention"
      ]
    },
    {
      icon: Network,
      title: "Network Security",
      description: "Robust network protection against sophisticated cyber threats and attacks.",
      items: [
        "Network monitoring",
        "Intrusion detection",
        "Firewall management",
        "Traffic analysis"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      description: "Secure cloud solutions meeting strict government security requirements.",
      items: [
        "FedRAMP compliance",
        "Cloud infrastructure security",
        "Data protection",
        "Access controls"
      ]
    }
  ];

  const compliance = [
    {
      icon: FileCheck,
      title: "Regulatory Compliance",
      items: [
        "FISMA compliance",
        "NIST framework",
        "Security controls",
        "Audit support"
      ]
    },
    {
      icon: Database,
      title: "Data Governance",
      items: [
        "Data classification",
        "Privacy protection",
        "Records management",
        "Compliance monitoring"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      items: [
        "Risk assessment",
        "Vulnerability management",
        "Threat mitigation",
        "Continuous monitoring"
      ]
    }
  ];

  const solutions = [
    {
      icon: Server,
      title: "Infrastructure Security",
      description: "Comprehensive protection for government IT infrastructure and systems.",
      features: [
        "24/7 infrastructure monitoring",
        "Security incident response",
        "System hardening",
        "Patch management"
      ]
    },
    {
      icon: Shield,
      title: "Cyber Defense",
      description: "Advanced cyber defense solutions against sophisticated threats.",
      features: [
        "Threat intelligence",
        "Attack prevention",
        "Security analytics",
        "Incident response"
      ]
    },
    {
      icon: Users,
      title: "Access Management",
      description: "Secure access control and identity management solutions.",
      features: [
        "Identity verification",
        "Role-based access",
        "Multi-factor authentication",
        "Session management"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Landmark className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Government Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced cybersecurity solutions designed specifically for government agencies, protecting critical infrastructure and sensitive data
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Compliance Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Compliance & Governance</h2>
            <p className="text-lg text-muted">
              Meeting strict government security standards and regulations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {compliance.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <item.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{item.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {item.items.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Solutions</h2>
            <p className="text-lg text-muted">
              Comprehensive security solutions for government agencies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{solution.description}</p>

                <ul className="space-y-3">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Agency</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our government security experts to discuss your agency's specific needs and discover how we can help protect your critical infrastructure
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Government;